var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FlexBox } from '@dixa/lib-shared-habla';
import moment from 'moment-timezone';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { flowsApi } from 'apis/flows';
import { getNextMonthWithYear, getPreviousMonthWithYear, } from 'components/settings/components/sections/openingHours/views/schedule/utils/date';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { useScheduleContext } from '../../../../schedule.context';
import styles from './calendar.module.scss';
import { Day } from './components/day/day';
import { getCurrentDate, getFirstDayOfWeek, getIsDayActive, getLastDayOfMonth, getStartAndEndDate, } from './utils/date';
import { getSchedulesForMonthAndDay } from './utils/schedule';
const fiveRowGrid = 7 * 5;
const sixRowGrid = 7 * 6;
const weekdays = [...Array(7)].map((_, index) => moment()
    .isoWeekday(index + 1)
    .format('dddd'));
export function Calendar({ monthAndYear }) {
    const { id } = useAppPathParams('/settings/businesshours/:id');
    const [openingHours, setOpeningHours] = useState([]);
    const { initialSchedule, modifiedListItems } = useScheduleContext();
    const days = getLastDayOfMonth(monthAndYear);
    const currentDate = getCurrentDate();
    const isPastMonth = moment.utc({ year: currentDate.year, month: currentDate.month }) >
        moment.utc(monthAndYear);
    const previousMonthAndYear = getPreviousMonthWithYear(monthAndYear);
    const nextMonthAndYear = getNextMonthWithYear(monthAndYear);
    const previousMonthDays = getLastDayOfMonth(previousMonthAndYear);
    const firstDayOfWeek = getFirstDayOfWeek(monthAndYear);
    const daysFromLastMonth = previousMonthDays - (previousMonthDays - firstDayOfWeek);
    const daysFromThisMonth = days;
    const daysFromNextMonth = 
    // If the first day is Saturday and there are 31 days in the month
    // we need to add an additional row.
    (daysFromLastMonth + daysFromThisMonth > fiveRowGrid
        ? sixRowGrid
        : fiveRowGrid) -
        (daysFromLastMonth + daysFromThisMonth);
    const [getPreview, { isLoading }] = flowsApi.useReadFlowsSchedulesPreviewMutation();
    const handleDateChange = useCallback((monthAndYear) => __awaiter(this, void 0, void 0, function* () {
        const response = yield getPreview({
            id,
            payload: Object.assign(Object.assign({}, getStartAndEndDate(monthAndYear.year, monthAndYear.month, daysFromLastMonth, daysFromNextMonth)), { opening_hours: Object.values(modifiedListItems).flatMap((listItems) => Object.values(listItems)), version: initialSchedule === null || initialSchedule === void 0 ? void 0 : initialSchedule.version, unique_holidays: true }),
        });
        if ('data' in response) {
            setOpeningHours(response.data.opening_hours);
        }
    }), [
        daysFromLastMonth,
        daysFromNextMonth,
        id,
        initialSchedule,
        modifiedListItems,
        getPreview,
    ]);
    useEffect(() => {
        handleDateChange(monthAndYear);
    }, [handleDateChange, monthAndYear]);
    const schedulesByMonthAndDay = useMemo(() => openingHours.reduce((acc, schedule) => {
        const date = moment.utc(schedule.date);
        const key = `${date.month()}/${date.date()}`;
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(schedule);
        return acc;
    }, {}), [openingHours]);
    return (_jsx("div", Object.assign({ className: styles.root }, { children: _jsxs(FlexBox, Object.assign({ flexDirection: "column", gap: "200", minWidth: "700px" }, { children: [_jsx("div", Object.assign({ className: styles.headers }, { children: weekdays.map((day) => (_jsx("span", { children: day }, day))) })), _jsxs("div", Object.assign({ className: styles.days }, { children: [Array.from(Array(daysFromLastMonth), (_, index) => {
                            const day = previousMonthDays - (firstDayOfWeek - (index + 1));
                            const schedules = getSchedulesForMonthAndDay(schedulesByMonthAndDay, previousMonthAndYear.month, day);
                            const isToday = getIsDayActive(currentDate, {
                                day,
                                month: previousMonthAndYear.month,
                                year: monthAndYear.year,
                            });
                            return (_jsx(Day, { day: day, schedules: schedules, isToday: isToday, isDisabled: true, isLoading: isLoading }, day));
                        }), Array.from(Array(daysFromThisMonth), (_, index) => {
                            const day = index + 1;
                            const schedules = getSchedulesForMonthAndDay(schedulesByMonthAndDay, monthAndYear.month, day);
                            const isToday = getIsDayActive(currentDate, Object.assign({ day }, monthAndYear));
                            const isDisabled = isPastMonth ||
                                (monthAndYear.year === currentDate.year &&
                                    monthAndYear.month === currentDate.month &&
                                    day < currentDate.day);
                            return (_jsx(Day, { day: day, schedules: schedules, isToday: isToday, isDisabled: isDisabled, isLoading: isLoading }, day));
                        }), Array.from(Array(daysFromNextMonth), (_, index) => {
                            const day = index + 1;
                            const schedules = getSchedulesForMonthAndDay(schedulesByMonthAndDay, nextMonthAndYear.month, day);
                            const isToday = getIsDayActive(currentDate, Object.assign({ day }, nextMonthAndYear));
                            return (_jsx(Day, { day: day, schedules: schedules, isToday: isToday, isDisabled: true, isLoading: isLoading }, day));
                        })] }))] })) })));
}
